/**
 * The user guide as something to search — for the command search's **Guide**
 * rows and the agent's `findInGuide` tool.
 *
 * ## Why the guide, and not a second map of the app
 *
 * "Where can I see fundamentals?" has one right answer, and it is already
 * written, in wording the owner approved: `docs/user-guide/index.html`. A list of
 * "where things are" kept here or in the agent's prompt would be a second copy
 * that drifts the first time a tab is renamed — Summary became AI Review on
 * 2026-10-20, and the guide was the one place that was changed with it. So both
 * readers search the guide itself, the copy shipped with this very build (and
 * this brand's wording, since `copy-guide.mjs` brands it per build).
 *
 * ## What an entry is
 *
 * The guide is cards (`section.card`, an `h2`) of blocks (`div.block`, usually
 * an `h3` with an id). Each block's `data-title` is the guide's own search
 * words — written for the guide's search box, which is exactly this job. One
 * entry per block; a block with no id of its own links to its card.
 *
 * Pure: parsing takes the HTML as text and searching takes the entries, so the
 * fetch lives in `api/guide-index.ts` and the tests need no network.
 */

export interface GuideEntry {
  /** The anchor to open the guide at. */
  anchor: string;
  /** The block's heading — `Screening on fundamentals`. */
  heading: string;
  /** Its card's heading — `Screener`. */
  section: string;
  /** The guide's own search words for the block. */
  keywords: string;
  /** The block's text, whitespace collapsed. */
  text: string;
}

const squash = (s: string | null | undefined) => (s ?? "").replace(/\s+/g, " ").trim();

/** A card's `h2` without its `— subtitle` span. */
function cardHeading(card: Element): string {
  const h2 = card.querySelector("h2");
  if (!h2) return "";
  const copy = h2.cloneNode(true) as Element;
  copy.querySelectorAll(".sub").forEach((s) => s.remove());
  return squash(copy.textContent);
}

/** Every block of the guide, in page order. */
export function parseGuide(html: string): GuideEntry[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out: GuideEntry[] = [];
  doc.querySelectorAll("section.card").forEach((card) => {
    const section = cardHeading(card);
    const cardId = card.getAttribute("id") ?? "";
    card.querySelectorAll("div.block").forEach((block) => {
      const h3 = block.querySelector("h3");
      const anchor = h3?.getAttribute("id") || block.getAttribute("id") || cardId;
      if (!anchor) return;
      out.push({
        anchor,
        heading: squash(h3?.textContent) || section,
        section,
        keywords: squash(block.getAttribute("data-title")),
        text: squash(block.textContent),
      });
    });
  });
  return out;
}

/**
 * Words that carry no subject — "where can I see", "how do I". Dropped before
 * matching, so the question matches on what it is about. Without this the
 * command search matched a typed question as one string and found nothing.
 */
const FILLER = new Set(
  (
    "a an the i me my we our you your it its is are am be do does did can could would should will " +
    "how what where which who why when to of in on at for from with by about into this that these those " +
    "there here see show find get open use go look looking want need please tell help me some any " +
    "and or not no"
  ).split(" "),
);

/** The words of a query that say what it is about, lower case. */
export function contentWords(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9&/+.%-]+/)
    .map((w) => w.replace(/^[.\-]+|[.\-]+$/g, ""))
    .filter((w) => w.length > 1 && !FILLER.has(w));
}

/** `fundamentals` matches `fundamental`, `screens` matches `screen`. */
function stem(word: string): string {
  return word.length > 4 && word.endsWith("s") ? word.slice(0, -1) : word;
}

function hits(haystack: string, word: string): boolean {
  return haystack.includes(stem(word));
}

/**
 * The blocks that answer a query, best first, at most `limit`.
 *
 * Every content word must appear somewhere in the block (heading, search words
 * or text) — an entry that matches one word of three is not an answer. Then
 * ranked: a word in the heading counts most, in the guide's search words next,
 * in the prose least.
 */
export function searchGuide(entries: readonly GuideEntry[], query: string, limit = 3): GuideEntry[] {
  const words = contentWords(query);
  if (words.length === 0) return [];
  const scored: { entry: GuideEntry; score: number }[] = [];
  for (const entry of entries) {
    const heading = entry.heading.toLowerCase();
    const keywords = entry.keywords.toLowerCase();
    const text = entry.text.toLowerCase();
    let score = 0;
    let all = true;
    for (const word of words) {
      const s = (hits(heading, word) ? 6 : 0) + (hits(keywords, word) ? 3 : 0) + (hits(text, word) ? 1 : 0);
      if (s === 0) {
        all = false;
        break;
      }
      score += s;
    }
    if (all) scored.push({ entry, score });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.entry);
}

/** What the agent reads of one entry: the text, cut to a size a tool result can carry. */
export function entryForAgent(entry: GuideEntry, chars = 1500): Record<string, string> {
  return {
    section: entry.section,
    heading: entry.heading,
    anchor: entry.anchor,
    text: entry.text.length > chars ? `${entry.text.slice(0, chars)}…` : entry.text,
  };
}
